import { api, ApiError } from './api';
import {
  FinanceExportType,
  FinancePeriodEntry,
  FinancePeriodEntryType,
  VatReportResponse,
} from '../types/finance';
import { FinanceOverviewResponse, OverviewQuery } from '../types/overview';
import { ProofRunResponse, ProofsListResponse, PROOF_STRIP_DAYS } from '../types/proofs';
import { ProviderPayout, ProviderPayoutListResponse, ProviderPayoutRequest } from '../types/payouts';

/**
 * #473 — `gmv` is customer turnover (net of refunds, fully refunded orders excluded) and
 * `platformRevenue` is Garagat's own income, the commission net of VAT. They are never the same
 * number and never share a label. Mirrors merchant-settlement's `FinanceSummaryResponse`.
 */
export interface FinanceSummaryResponse {
  gmv: number;
  platformRevenue: number;
  /**
   * Spec 068 V14 — Garagat's service fee revenue for the period (net kept): fees charged, less fees
   * refunded to customers, plus fees recharged to garages, net of VAT. Optional on the wire: a
   * settlement build before Spec 068 omits it.
   */
  serviceFeeRevenue?: number | null;
  vatOnCommission: number;
  /** Charged at completion; not reduced by later refunds — the filed figure is the VAT return's. */
  customerVatTotal: number;
  totalRefunds: number;
  settlementsPaid: number;
  outstandingBalance: number;
  totalOrders: number;
  totalSettlements: number;
}

export interface RevenueTrendPoint {
  date: string;
  /** Turnover for the Dubai day, net of refunds — NOT revenue (#473). */
  gmv: number;
  /** Garagat's revenue for the day: commission, net of VAT. */
  commission: number;
  orderCount: number;
}

export interface TopGarageResponse {
  garageId: string;
  /** Resolved from garages-service; null when that lookup failed (#562). */
  garageName: string | null;
  gmv: number;
  orderCount: number;
  commissionEarned: number;
}

export interface OutstandingBalanceResponse {
  garageId: string;
  garageName: string | null;
  outstandingAmount: number;
  pendingOrderCount: number;
  oldestUnsettledDate: string | null;
}

export interface SettlementOverviewResponse {
  pendingCount: number;
  pendingValue: number;
  paidCount: number;
  paidValue: number;
}

/** Spec 053 Flow C — manual provider VAT figure for a period (Stripe invoice total / Paymob input VAT). */
export interface CreatePeriodEntryInput {
  period: string;
  type: FinancePeriodEntryType;
  amount: number;
  /** The provider's invoice; required for STRIPE_VAT_INVOICE, recommended for PAYMOB_INPUT_VAT. */
  document?: File | null;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

async function uploadMultipart<T>(url: string, formData: FormData, token: string): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}` },
    body: formData,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new ApiError(err.message || err.error || `HTTP ${response.status}`, response.status);
  }
  return response.json() as Promise<T>;
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8) return decodeURIComponent(utf8[1]);
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1] : null;
}

/** The `message` of a JSON error body (AdminFinanceController / GlobalExceptionHandler shape), else a generic HTTP line. */
async function exportErrorMessage(response: Response): Promise<string> {
  const body: { message?: unknown; error?: unknown } = await response.json().catch(() => ({}));
  if (typeof body.message === 'string' && body.message) return body.message;
  if (typeof body.error === 'string' && body.error) return body.error;
  return `HTTP ${response.status}`;
}

/**
 * Last resort only (#533). The server names every export
 * (`garagat-<type>-<period>[-provisional].csv`) and the gateway now exposes `Content-Disposition`,
 * so this is reached only if the header is genuinely absent — and then anything we invent would be
 * a guess. It used to be the normal path: the portal rebuilt the name from `type`/`period` and got
 * it wrong, because the client copy never carried the `garagat-` prefix. Without an
 * `anchor.download` value the browser falls back to the last URL segment (`vat-summary`, no
 * extension), which is worse than a generic name, so one generic name is kept.
 */
const LAST_RESORT_EXPORT_FILENAME = 'garagat-export.csv';

/**
 * The gateway validates the Bearer token, so a plain anchor cannot carry it: fetch → blob →
 * object URL → synthetic anchor click. A refusal comes back as JSON, not CSV — 413 "Export too
 * large — narrow the period", 503 when billing is unreachable — so both media types are accepted
 * and the body's `message` is surfaced. Returns the filename that was saved, which is the server's
 * (`Content-Disposition`) — the portal no longer keeps its own copy of that naming contract.
 */
async function downloadCsv(url: string, token: string): Promise<string> {
  const response = await fetch(`${API_BASE}${url}`, {
    headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'text/csv, application/json' },
  });
  if (!response.ok) {
    throw new ApiError(await exportErrorMessage(response), response.status);
  }
  const blob = await response.blob();
  const filename = filenameFromDisposition(response.headers.get('Content-Disposition'))
    ?? LAST_RESORT_EXPORT_FILENAME;
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  return filename;
}

export const financeApi = {
  getSummary: (dateFrom: string, dateTo: string, token: string) =>
    api.get<FinanceSummaryResponse>(
      `/api/admin/finance/summary?dateFrom=${dateFrom}&dateTo=${dateTo}`, token
    ),

  getRevenueTrend: (dateFrom: string, dateTo: string, token: string) =>
    api.get<RevenueTrendPoint[]>(
      `/api/admin/finance/revenue-trend?dateFrom=${dateFrom}&dateTo=${dateTo}`, token
    ),

  getTopGarages: (limit: number, dateFrom: string, dateTo: string, token: string) =>
    api.get<TopGarageResponse[]>(
      `/api/admin/finance/top-garages?limit=${limit}&dateFrom=${dateFrom}&dateTo=${dateTo}`, token
    ),

  getOutstandingBalances: (token: string) =>
    api.get<OutstandingBalanceResponse[]>(
      '/api/admin/finance/outstanding-balances', token
    ),

  getSettlementOverview: (token: string) =>
    api.get<SettlementOverviewResponse>(
      '/api/admin/finance/settlement-overview', token
    ),

  // ── Spec 053 Flow C — month-end VAT and finance pack ─────────────────────────

  /** Period is `YYYY-MM` in the Asia/Dubai calendar (BR-20). */
  getVatReport: (period: string, token: string) =>
    api.get<VatReportResponse>(`/api/admin/finance/reports/vat?period=${period}`, token),

  listPeriodEntries: (period: string, token: string) =>
    api.get<FinancePeriodEntry[]>(`/api/admin/finance/period-entries?period=${period}`, token),

  /** AC-13b — multipart { period, type, amount, document } → 201. UNIQUE (period, type): re-posting replaces. */
  createPeriodEntry: (input: CreatePeriodEntryInput, token: string) => {
    const form = new FormData();
    form.append('period', input.period);
    form.append('type', input.type);
    form.append('amount', input.amount.toFixed(2));
    if (input.document) form.append('document', input.document);
    return uploadMultipart<FinancePeriodEntry>('/api/admin/finance/period-entries', form, token);
  },

  /**
   * AC-15 — CSV export for a `YYYY-MM` period. The backend audit-logs the export (actor, type,
   * period), names the file and suffixes `-provisional` when the period is still open.
   */
  downloadExport: (type: FinanceExportType, period: string, token: string): Promise<string> =>
    downloadCsv(`/api/admin/finance/exports/${type}?period=${period}`, token),

  /**
   * Flow C2 step 3 — CSV export for a date range (`overview`: position as at `to`, flows for the
   * range). The backend decides the `-provisional` suffix from `to`, as for the period export.
   */
  downloadRangeExport: (type: FinanceExportType, from: string, to: string, token: string): Promise<string> =>
    downloadCsv(`/api/admin/finance/exports/${type}?from=${from}&to=${to}`, token),

  // ── Spec 053 Flow C2 / Flow E — overview and nightly proofs (Phase 2) ───────

  /** Position as at `asAt`, flows for `from..to` (all Dubai dates). 200 + `notice` before ledger go-live, never 4xx. */
  getOverview: ({ asAt, from, to }: OverviewQuery, token: string) =>
    api.get<FinanceOverviewResponse>(`/api/admin/finance/overview?asAt=${asAt}&from=${from}&to=${to}`, token),

  /** The last `days` Dubai dates with each night's three proof runs (days before go-live omitted). */
  listProofs: (token: string, days: number = PROOF_STRIP_DAYS) =>
    api.get<ProofsListResponse>(`/api/admin/finance/proofs?days=${days}`, token),

  getProofRun: (runId: string, token: string) =>
    api.get<ProofRunResponse>(`/api/admin/finance/proofs/${encodeURIComponent(runId)}`, token),

  /**
   * Runs all three proofs for `date` (default yesterday on the backend) and replaces that date's
   * rows; 400 when the date is in the future or before go-live. Today's run takes a fresh Stripe
   * snapshot first, so it can take a few seconds.
   */
  runProofs: (date: string, token: string) =>
    api.post<ProofRunResponse[]>(`/api/admin/finance/proofs/run?date=${date}`, undefined, token),

  // ── Spec 067 DD4-A — Tabby payouts recorded by hand ─────────────────────────

  /** Latest 50 recorded Tabby payouts and the default payout fee (Runtime Config TABBY_PAYOUT_FEE_AED). */
  listProviderPayouts: (token: string, provider: 'TABBY' = 'TABBY') =>
    api.get<ProviderPayoutListResponse>(`/api/admin/finance/provider-payouts?provider=${provider}`, token),

  /** 201 with the payout; 409 when the reference is already recorded; 400 on a bad field. */
  recordProviderPayout: (body: ProviderPayoutRequest, token: string) =>
    api.post<ProviderPayout>('/api/admin/finance/provider-payouts', body, token),
};
